import React, { useMemo } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { classColorTokens } from '../../utils/classColors';
import { colors } from '../../theme/theme';
import type { DrawerBooking } from './SessionBookingsDrawer';
import type { ScheduleSession } from './ScheduleCalendarView';
import { MemberNoteIcon, dueLabel, isPaymentPending } from './bookingRowBits';
import type { BookingNotes } from './bookingRowBits';
import { SCHED, btnSx, iconBtnSx } from './scheduleTheme';

const IST_TIMEZONE = 'Asia/Kolkata';
const HOUR_PX = 176;
// A 30-minute class keeps its true height; anything shorter is stretched so it stays readable.
const MIN_EVENT_PX = 88;
const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 21;

const istMinutes = (d: Date) => {
  const [h, m] = formatInTimeZone(d, IST_TIMEZONE, 'H:m').split(':').map(Number);
  return h * 60 + m;
};
const timeLabel = (d: Date) => formatInTimeZone(d, IST_TIMEZONE, 'h:mm a').toUpperCase();
const hourLabel = (h: number) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);

interface PlacedSession {
  s: ScheduleSession;
  top: number;
  height: number;
  col: number;
  cols: number;
}

/** Lays out one day's sessions: overlapping ones share the width side by side. */
const layoutDay = (list: ScheduleSession[], startHour: number): PlacedSession[] => {
  const items = list
    .map((s) => {
      const start = istMinutes(new Date(s.date));
      const height = Math.max(((s.duration || 30) / 60) * HOUR_PX, MIN_EVENT_PX);
      // Visual end, so blocks stretched to the minimum height don't overlap.
      return { s, start, top: (start / 60 - startHour) * HOUR_PX, height, end: start + (height / HOUR_PX) * 60 };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const placed: PlacedSession[] = [];
  let cluster: (PlacedSession & { end: number })[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    cluster.forEach((p) => placed.push({ s: p.s, top: p.top, height: p.height, col: p.col, cols: colEnds.length }));
    cluster = [];
    colEnds = [];
  };
  items.forEach((it) => {
    if (it.start >= clusterEnd) flush();
    let col = colEnds.findIndex((end) => end <= it.start);
    if (col === -1) {
      col = colEnds.length;
      colEnds.push(it.end);
    } else {
      colEnds[col] = it.end;
    }
    cluster.push({ s: it.s, top: it.top, height: it.height, col, cols: 0, end: it.end });
    clusterEnd = Math.max(clusterEnd, it.end);
  });
  flush();
  return placed;
};

const isAttending = (b: DrawerBooking) => b.bookingStatus !== 'CANCELLED' && b.bookingStatus !== 'NO_SHOW';

// "Meghna K." where there is room, "Meghna" in a narrow side-by-side block.
const chipName = (name: string | undefined, short: boolean) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'Member';
  return short || parts.length === 1 ? parts[0] : `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

interface ScheduleDayViewProps {
  isToday: boolean;
  dateLabel: string;
  sessions: ScheduleSession[];
  bookingsBySession: Record<string, DrawerBooking[]>;
  waitingBySession: Record<string, number> | null;
  notes: BookingNotes;
  now: Date;
  selectedSessionId: string | null;
  colorOf: (s: ScheduleSession) => string;
  onOpenSession: (id: string) => void;
  onShiftDay: (dir: -1 | 1) => void;
  onAddSessions?: () => void;
  emptyLabel: string;
}

const ScheduleDayView: React.FC<ScheduleDayViewProps> = ({
  isToday,
  dateLabel,
  sessions,
  bookingsBySession,
  waitingBySession,
  notes,
  now,
  selectedSessionId,
  colorOf,
  onOpenSession,
  onShiftDay,
  onAddSessions,
  emptyLabel,
}) => {
  const { startHour, endHour } = useMemo(() => {
    const starts = sessions.map((s) => istMinutes(new Date(s.date)));
    const ends = sessions.map((s, i) => starts[i] + (s.duration || 30));
    return {
      startHour: Math.min(DEFAULT_START_HOUR, ...starts.map((m) => Math.floor(m / 60))),
      endHour: Math.min(24, Math.max(DEFAULT_END_HOUR, ...ends.map((m) => Math.ceil(m / 60)))),
    };
  }, [sessions]);
  const placed = useMemo(() => layoutDay(sessions, startHour), [sessions, startHour]);
  const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);

  const live = sessions.filter((s) => s.isLive !== false);
  const slots = live.reduce((n, s) => n + (s.slots || 0), 0);
  const booked = live.reduce((n, s) => n + Math.max(0, s.slots - (s.availableSlots ?? s.slots)), 0);
  const waiting = waitingBySession ? live.reduce((n, s) => n + (waitingBySession[s._id] || 0), 0) : null;
  const teachers = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    sessions.forEach((s) => {
      if (s.isLive === false || !s.instructor?.name) return;
      const row = counts.get(s.instructor._id) || { name: s.instructor.name, count: 0 };
      counts.set(s.instructor._id, { ...row, count: row.count + 1 });
    });
    return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [sessions]);

  const nowMinutes = istMinutes(now);
  const showNow = isToday && nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60;

  const statBox = (value: number | string, label: string) => (
    <Box sx={{ p: '12px 14px', borderRadius: '14px', backgroundColor: SCHED.canvas }}>
      <Box sx={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.2 }}>{value}</Box>
      <Box sx={{ fontSize: '13px', color: SCHED.muted }}>{label}</Box>
    </Box>
  );

  const renderCard = ({ s, top, height, col, cols }: PlacedSession) => {
    const t = classColorTokens(colorOf(s));
    const cancelled = s.isLive === false;
    const start = new Date(s.date);
    const end = new Date(start.getTime() + (s.duration || 0) * 60000);
    const ended = end < now;
    const wide = cols === 1;
    const list = bookingsBySession[s._id];
    // Owed money first, so a pending payment is never hidden behind the "+N".
    const attending = (list || []).filter(isAttending).sort((a, b) => Number(isPaymentPending(b)) - Number(isPaymentPending(a)));
    const checkedInCount = (list || [])
      .filter((b) => b.bookingStatus === 'CHECKED_IN')
      .reduce((n, b) => n + (b.quantity ?? 1), 0);
    const bookedCount = Math.max(0, s.slots - (s.availableSlots ?? s.slots));
    const full = (s.availableSlots ?? 1) === 0;
    const wait = waitingBySession?.[s._id] || 0;
    const showCheckedIn = ended && !cancelled && Boolean(list);
    const status = cancelled
      ? 'Cancelled'
      : showCheckedIn
      ? `${checkedInCount} checked in`
      : `${full ? 'Full · ' : ''}${bookedCount}/${s.slots} booked`;
    const fill = s.slots > 0 ? Math.min(100, ((showCheckedIn ? checkedInCount : bookedCount) / s.slots) * 100) : 0;
    const sub = [
      s.instructor?.name ? (wide ? `With ${s.instructor.name}` : s.instructor.name) : null,
      s.classType?.title || s.classType?.name,
    ]
      .filter(Boolean)
      .join(' · ');

    const maxChips = wide ? 8 : 5;
    const fitsChips = height >= (wide ? 84 : 118);
    const chips = fitsChips && !cancelled && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '6px' }}>
        {attending.slice(0, maxChips).map((b) => {
          const checkedIn = b.bookingStatus === 'CHECKED_IN';
          const pending = isPaymentPending(b);
          return (
            <Box
              key={b._id}
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: 1,
                p: '6px 9px',
                borderRadius: '999px',
                whiteSpace: 'nowrap',
                backgroundColor: checkedIn ? SCHED.successBg : '#FFFFFF',
                color: checkedIn ? SCHED.successText : SCHED.ink,
              }}
            >
              {checkedIn ? '✓ ' : ''}
              {chipName(b.user?.name, !wide)}
              {b.bookingType === 'trial' && <Box component="span" sx={{ fontWeight: 800 }}>Trial</Box>}
              {pending && (
                <Box component="span" sx={{ fontSize: '11px', p: '2px 6px', borderRadius: '999px', backgroundColor: colors.warning, color: colors.white }}>
                  {dueLabel(b)}
                </Box>
              )}
              <MemberNoteIcon notes={notes} booking={b} size={14} existingOnly />
            </Box>
          );
        })}
        {attending.length > maxChips && (
          <Box component="span" sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1, p: '6px 4px', color: SCHED.inkSoft }}>
            +{attending.length - maxChips}
          </Box>
        )}
      </Box>
    );

    const bar = !cancelled && (
      <Box sx={{ width: wide ? 140 : 64, flexShrink: 1, minWidth: 24, height: 5, borderRadius: '3px', backgroundColor: 'rgba(255,255,255,0.8)', overflow: 'hidden' }}>
        <Box sx={{ width: `${fill}%`, height: '100%', backgroundColor: t.accent }} />
      </Box>
    );
    const waitLabel = !cancelled && !ended && wait > 0 && (
      <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, color: SCHED.waitText, whiteSpace: 'nowrap' }}>
        {wait} on waitlist
      </Box>
    );
    const statusText = (
      <Typography noWrap sx={{ fontFamily: SCHED.font, fontSize: '13px', fontWeight: 700, color: cancelled ? SCHED.muted : SCHED.ink }}>
        {status}
      </Typography>
    );

    return (
      <Box
        key={s._id}
        role="button"
        tabIndex={0}
        onClick={() => onOpenSession(s._id)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenSession(s._id);
          }
        }}
        sx={{
          position: 'absolute',
          top: top + 2,
          height: height - 4,
          left: `calc(${(col / cols) * 100}% + 4px)`,
          width: `calc(${100 / cols}% - 8px)`,
          boxSizing: 'border-box',
          backgroundColor: cancelled ? SCHED.cancelledTint : t.bg,
          borderLeft: `4px solid ${cancelled ? SCHED.muted : t.accent}`,
          borderRadius: '14px',
          p: height < 110 ? '8px 14px' : '12px 14px',
          overflow: 'hidden',
          cursor: 'pointer',
          zIndex: 1,
          outline: selectedSessionId === s._id ? `2px solid ${SCHED.ink}` : 'none',
          outlineOffset: '-2px',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: cancelled ? SCHED.track : t.bgHover, zIndex: 2 },
          '&:focus-visible': { outline: `2px solid ${SCHED.ink}` },
        }}
      >
        {wide ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr) auto', columnGap: '16px', alignItems: 'start' }}>
            <Box>
              <Box sx={{ fontSize: '15px', fontWeight: 700, color: SCHED.ink, textDecoration: cancelled ? 'line-through' : 'none' }}>{timeLabel(start)}</Box>
              <Box sx={{ fontSize: '12px', color: SCHED.muted, whiteSpace: 'nowrap' }}>{timeLabel(start)} to {timeLabel(end)}</Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: SCHED.font, fontSize: '15px', fontWeight: 700, color: cancelled ? SCHED.muted : t.text, textDecoration: cancelled ? 'line-through' : 'none' }}>
                {s.className}
              </Typography>
              {sub && <Typography noWrap sx={{ fontFamily: SCHED.font, fontSize: '13px', color: SCHED.inkSoft }}>{sub}</Typography>}
              {chips}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {statusText}
                {bar}
                {waitLabel}
              </Box>
              {ended && !cancelled && <Box component="span" sx={{ fontSize: '12px', fontWeight: 600, color: SCHED.muted, mt: '10px' }}>Done</Box>}
            </Box>
          </Box>
        ) : (
          <>
            <Typography noWrap sx={{ fontFamily: SCHED.font, fontSize: '14px', fontWeight: 700, color: cancelled ? SCHED.muted : t.text }}>
              <Box component="span" sx={{ color: SCHED.ink, mr: '6px' }}>{timeLabel(start)}</Box>
              {s.className}
            </Typography>
            {sub && <Typography noWrap sx={{ fontFamily: SCHED.font, fontSize: '12px', color: SCHED.inkSoft }}>{sub}</Typography>}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '2px', minWidth: 0 }}>
              {statusText}
              {cols < 3 && bar}
              {cols < 3 && waitLabel}
            </Box>
            {chips}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 300px' }, gap: '16px', alignItems: 'start' }}>
      <Box sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${SCHED.border}`, borderRadius: '20px', p: { xs: '16px', md: '20px 22px' }, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: '12px' }}>
          <Box component="h2" sx={{ m: 0, fontSize: '20px', fontWeight: 700 }}>{dateLabel}</Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <IconButton onClick={() => onShiftDay(-1)} aria-label="Previous day" sx={iconBtnSx(40)}>
              <ChevronLeft sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton onClick={() => onShiftDay(1)} aria-label="Next day" sx={iconBtnSx(40)}>
              <ChevronRight sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {sessions.length === 0 && (
          <Box sx={{ fontSize: '14px', color: SCHED.muted, textAlign: 'center', py: '14px' }}>{emptyLabel}</Box>
        )}

        <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <Box sx={{ position: 'relative', minWidth: 560, py: '12px' }}>
            <Box sx={{ position: 'relative', height: (endHour - startHour) * HOUR_PX }}>
              {hours.map((h) => (
                <Box key={h} sx={{ position: 'absolute', top: (h - startHour) * HOUR_PX, left: 0, right: 0, display: 'flex', alignItems: 'center', height: 0 }}>
                  <Box component="span" sx={{ width: 56, flexShrink: 0, fontSize: '13px', color: SCHED.muted }}>{hourLabel(h)}</Box>
                  <Box sx={{ flexGrow: 1, borderTop: `1px solid ${SCHED.rowLine}` }} />
                </Box>
              ))}
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 64, right: 0 }}>
                {placed.map(renderCard)}
              </Box>
              {showNow && (
                <Box sx={{ position: 'absolute', top: (nowMinutes / 60 - startHour) * HOUR_PX, left: 56, right: 0, height: 2, backgroundColor: SCHED.primary, zIndex: 3, pointerEvents: 'none' }}>
                  <Box component="span" sx={{ position: 'absolute', left: 0, top: -9, fontSize: '11px', fontWeight: 700, lineHeight: 1, p: '4px 7px', borderRadius: '999px', backgroundColor: SCHED.primary, color: '#FFFFFF', whiteSpace: 'nowrap' }}>
                    NOW {formatInTimeZone(now, IST_TIMEZONE, 'h:mm')}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', position: { lg: 'sticky' }, top: { lg: 16 } }}>
        <Box sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${SCHED.border}`, borderRadius: '20px', p: '18px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
            <Box sx={{ fontSize: '16px', fontWeight: 700 }}>This day</Box>
            <Box sx={{ fontSize: '13px', color: SCHED.muted }}>{slots > 0 ? Math.round((booked / slots) * 100) : 0}% full</Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {statBox(live.length, 'classes')}
            {statBox(booked, 'booked')}
            {statBox(Math.max(0, slots - booked), 'spots left')}
            {statBox(waiting ?? '–', 'waiting')}
          </Box>
        </Box>

        {teachers.length > 0 && (
          <Box sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${SCHED.border}`, borderRadius: '20px', p: '18px' }}>
            <Box sx={{ fontSize: '16px', fontWeight: 700, mb: '6px' }}>Who is teaching</Box>
            {teachers.map((row) => (
              <Box key={row.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', py: '10px', borderBottom: `1px solid ${SCHED.rowLine}`, fontSize: '14px' }}>
                <Box component="span" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</Box>
                <Box component="span" sx={{ color: SCHED.muted, whiteSpace: 'nowrap' }}>{row.count} {row.count === 1 ? 'class' : 'classes'}</Box>
              </Box>
            ))}
          </Box>
        )}

        {onAddSessions && (
          <Box component="button" type="button" onClick={onAddSessions} sx={{ ...btnSx, width: '100%', cursor: 'pointer' }}>
            + Add sessions
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScheduleDayView;
